'use client';

import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { PropsWithChildren, createContext } from 'react';

import { getUserProfileApi, getUserProfileByProfileIdApi } from '@flipster/apis/v1/user';
import { overlayBunja } from '@flipster/overlay';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';

import { authBunja } from '../shared/auth/state';
import { followBunja } from '../shared/community/follow';
import { profileSettingBunja } from '../shared/community/profile';
import EditProfileModal from './edit-profile-modal';
import { oauthProviderSearchParamKey } from './edit-profile-modal/shared/oauth';

export const UserProfileQueryKey = ['user', 'profile'];
export const UserFollowQueryKey = ['user', 'follow'];
export function getUserProfileQueryKey(profileId: string | undefined, isLoggedIn: boolean) {
  return [...UserProfileQueryKey, profileId, isLoggedIn];
}
export const ProfileContext = createContext<string | undefined>(undefined);
export const ProfileScope = createScopeFromContext(ProfileContext);
export const profileBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);
  const profileId = bunja.use(ProfileScope);
  const { isLoggedInAtom, userAtom } = bunja.use(authBunja);
  const { putUserProfileMutationAtom } = bunja.use(profileSettingBunja);
  const { postUserFollowMutationAtom, deleteUserFollowMutationAtom } = bunja.use(followBunja);

  const baseProfileAtom = atomWithQuery((get) => {
    const isLoggedIn = get(isLoggedInAtom);
    // if profileScope is empty, load my profile
    if (profileId == null) {
      return {
        queryKey: getUserProfileQueryKey(profileId, isLoggedIn),
        queryFn: getUserProfileApi,
        enabled: isLoggedIn,
        experimental_prefetchInRender: true,
      };
    }
    return {
      queryKey: getUserProfileQueryKey(profileId, isLoggedIn),
      queryFn: () => getUserProfileByProfileIdApi(profileId),
    };
  });
  const profileAtom = atom((get) => get(baseProfileAtom).data?.data);
  const profileSettingsAtom = atom((get) => get(profileAtom)?.settings);
  const isMyProfileAtom = atom((get) => profileId != null || get(profileAtom)?.isMyProfile);
  const notExistProfileAtom = atom((get) => {
    const { error } = get(baseProfileAtom);
    return getErrorCode(error) === 'api.user.profile.notExist';
  });
  const myProfileIdAtom = atom((get) => get(userAtom)?.profileId ?? '');
  const resolvedProfileIdAtom = atom((get) => profileId ?? get(myProfileIdAtom));

  async function refetchProfile() {
    const { refetch } = store.get(baseProfileAtom);
    await refetch();
  }

  // for processing oauth callback we should open modal first
  // see ./edit-profile-modal/bunja.ts
  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(oauthProviderSearchParamKey)) {
      const { promise } = store.get(baseProfileAtom);
      promise.then(() => overlay.open(<EditProfileModal />));
    }
  });

  return {
    profileId,
    profileAtom,
    isMyProfileAtom,
    myProfileIdAtom,
    resolvedProfileIdAtom,
    profileSettingsAtom,
    notExistProfileAtom,
    putUserProfileMutationAtom,
    postUserFollowMutationAtom,
    deleteUserFollowMutationAtom,
    // fns
    refetchProfile,
  };
});

// https://github.com/vercel/next.js/issues/75992
export const ProfileContextProvider = ({ children, value }: PropsWithChildren<{ value: string }>) => {
  return <ProfileContext value={value}>{children}</ProfileContext>;
};
